import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
    ScatterChart, Scatter, CartesianGrid,
    XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Label
} from 'recharts';
import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
    Paper,
    Stack,
    useTheme,
    alpha
} from '@mui/material';

const CHART_PALETTE = [
    '#2563EB', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#E11D48',
    '#14B8A6', '#7C3AED', '#0EA5E9', '#22C55E', '#F43F5E',
    '#6366F1', '#A855F7', '#D946EF', '#3B82F6', '#16A34A',
    '#F97316', '#CA8A04', '#DC2626', '#0F766E', '#334155'
];

const PIE_COLORS = [
    '#2563EB', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#E11D48',
    '#14B8A6', '#7C3AED', '#0EA5E9', '#22C55E', '#F43F5E',
    '#6366F1', '#A855F7', '#D946EF', '#3B82F6', '#16A34A',
    '#FB7185', '#A3E635', '#FACC15', '#BE123C', '#1D4ED8'
];

const formatNumber = (num) => {
    if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    if (Number.isInteger(num)) return num.toLocaleString();
    return num.toFixed(2);
};

const CustomTooltip = ({ active, payload, label }) => {
    // eslint-disable-next-line react/prop-types
    if (active && payload?.length) {
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    minWidth: 160,
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700, color: '#1E293B', fontSize: 13 }}>
                    {label}
                </Typography>
                {payload.map((entry) => (
                    <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: entry.color, mr: 1, flexShrink: 0
                        }} />
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: 12, mr: 1 }}>
                            {entry.name}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: 12 }}>
                            {formatNumber(entry.value)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }
    return null;
};

const RoundedBar = (props) => {
    const { x, y, width, height, fill } = props;
    const safeHeight = Math.abs(height);
    const safeY = height < 0 ? y + height : y;
    const radius = Math.min(6, width / 2);

    if (safeHeight < radius) {
        return <rect x={x} y={safeY} width={width} height={safeHeight} fill={fill} />;
    }

    return (
        <path
            d={`M${x},${safeY + safeHeight} 
                L${x},${safeY + radius} 
                Q${x},${safeY} ${x + radius},${safeY} 
                L${x + width - radius},${safeY} 
                Q${x + width},${safeY} ${x + width},${safeY + radius} 
                L${x + width},${safeY + safeHeight} Z`}
            fill={fill}
        />
    );
};

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central" fontSize={11} fontWeight={500}>
            {name} ({(percent * 100).toFixed(1)}%)
        </text>
    );
};

// Extracted CustomTick component
const CustomTick = (props) => {
    // eslint-disable-next-line react/prop-types
    const { x, y, payload } = props;
    // eslint-disable-next-line react/prop-types
    let displayText = payload.value;
    // eslint-disable-next-line react/prop-types
    if (displayText && displayText.length > 12) {
        // eslint-disable-next-line react/prop-types
        displayText = displayText.substring(0, 10) + '...';
    }
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end"
                fill="#64748B" fontSize={11} fontWeight={500} transform="rotate(-45)">
                {displayText}
            </text>
        </g>
    );
};

// Extracted CustomYTick component
const CustomYTick = (props) => {
    // eslint-disable-next-line react/prop-types  
    const { x, y, payload } = props;
    const fmt = (num) => {
        if (num >= 1000000000) return (num / 1000000000).toFixed(0) + 'B';
        if (num >= 1000000) return (num / 1000000).toFixed(0) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
        return num.toString();
    };
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dx={-8} textAnchor="end"
                fill="#64748B" fontSize={11} fontWeight={500}>
                {/* eslint-disable-next-line react/prop-types */}
                {fmt(payload.value)}
            </text>
        </g>
    );
};

// Extracted LegendFormatter component
const LegendFormatter = (value) => (
    <span style={{ color: '#475569', fontWeight: 500, fontSize: 12 }}>{value}</span>
);

// Map tool names to human-readable labels
const ENDPOINT_LABELS = {
    'get_profile_analytics': 'Profile Analytics',
    'get_post_analytics': 'Post Analytics',
    'get_topic_metrics': 'Topic Metrics',
    'get_topic_messages': 'Topic Messages',
    'trend': 'Trend Analysis',
    'search': 'Search Results',
    'queries': 'Query Data',
};

const formatEndpointLabel = (endpoint) => {
    return ENDPOINT_LABELS[endpoint] || endpoint.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const toLabel = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const parseNumericValue = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const normalized = value
            .trim()
            .replace(/[$,%\s]/g, '')
            .replace(/,/g, '');
        if (!/^[-+]?\d*\.?\d+$/.test(normalized)) return null;
        const parsed = Number.parseFloat(normalized);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
};

const parseDateValue = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
};

const toDayKey = (dateObj) => {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const toMonthKey = (dateObj) => {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`;
};

const getMetricQualityScore = (fieldName, values) => {
    if (!values || values.length < 2) return -Infinity;
    const lower = String(fieldName || '').toLowerCase();
    const likelyIdField = /(id|index|rank|code)$/.test(lower);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((acc, v) => acc + (v - avg) ** 2, 0) / values.length;
    const spread = Math.sqrt(Math.max(variance, 0));
    const nonZero = values.filter((v) => v !== 0).length;
    const nonZeroRatio = nonZero / values.length;
    const score = spread * (0.5 + nonZeroRatio);
    return likelyIdField ? score * 0.1 : score;
};

const DynamicBarChart = ({ toolResults }) => {
    const theme = useTheme();
    
    // Get available endpoints from toolResults
    const getAvailableEndpoints = () => {
        // eslint-disable-next-line react/prop-types
        if (!toolResults || toolResults.length === 0) return [];
        // eslint-disable-next-line react/prop-types
        return [...new Set(toolResults.map(result => result?.endpoint).filter(Boolean))];
    };
    
    const availableEndpoints = getAvailableEndpoints();
    const [selectedEndpoint, setSelectedEndpoint] = useState('');
    
    // Filter toolResults by selected endpoint
    const filteredToolResults = useMemo(() => (
        // eslint-disable-next-line react/prop-types
        toolResults?.filter(result => !selectedEndpoint || result?.endpoint === selectedEndpoint) || []
    ), [selectedEndpoint, toolResults]);

    // Merge all selected endpoint payload rows instead of only first payload.
    const chartData = useMemo(() => {
        if (!filteredToolResults.length) return [];
        const rows = [];

        filteredToolResults.forEach((result) => {
            if (!result?.content) return;
            const raw = result.endpoint === 'trend' ? result.content?.social : result.content;
            if (Array.isArray(raw)) {
                raw.forEach((item) => {
                    if (item && typeof item === 'object') rows.push(item);
                });
            } else if (raw && typeof raw === 'object') {
                rows.push(raw);
            }
        });

        return rows;
    }, [filteredToolResults]);

    const chartSuggestions = useMemo(() => {
        if (!filteredToolResults.length) return [];
        return filteredToolResults.flatMap((result) => Array.isArray(result?.chart_suggestions) ? result.chart_suggestions : []);
    }, [filteredToolResults]);

    const fieldStats = useMemo(() => {
        if (!chartData.length) return {};
        const stats = {};

        chartData.forEach((row) => {
            Object.keys(row || {}).forEach((key) => {
                if (!stats[key]) {
                    stats[key] = {
                        numericCount: 0,
                        dateCount: 0,
                        stringCount: 0,
                        labelCount: 0,
                        seen: 0,
                        numericValues: [],
                    };
                }
                const value = row[key];
                if (value === undefined || value === null || value === '') return;

                stats[key].seen += 1;
                const numericValue = parseNumericValue(value);
                const dateValue = parseDateValue(value);

                if (numericValue !== null) {
                    stats[key].numericCount += 1;
                    stats[key].numericValues.push(numericValue);
                }
                if (dateValue) {
                    stats[key].dateCount += 1;
                }
                if (typeof value === 'string') {
                    stats[key].stringCount += 1;
                    // Label datapoints are textual strings that are not numeric values.
                    if (numericValue === null) {
                        stats[key].labelCount += 1;
                    }
                }
            });
        });

        return stats;
    }, [chartData]);

    const numericFields = useMemo(() => {
        return Object.keys(fieldStats).filter((key) => {
            const stat = fieldStats[key];
            return stat.numericCount >= Math.max(2, Math.floor(stat.seen * 0.5));
        });
    }, [fieldStats]);

    const dateFields = useMemo(() => {
        return Object.keys(fieldStats).filter((key) => {
            const stat = fieldStats[key];
            return stat.dateCount >= Math.max(2, Math.floor(stat.seen * 0.6));
        });
    }, [fieldStats]);

    const categoricalFields = useMemo(() => {
        const keys = Object.keys(fieldStats).filter((key) => {
            const stat = fieldStats[key];
            const mostlyLabels = stat.labelCount >= Math.max(2, Math.floor(stat.seen * 0.5));
            return mostlyLabels || dateFields.includes(key);
        });

        // Keep date fields first for business trend analysis.
        return [...keys].sort((a, b) => {
            const aDate = dateFields.includes(a) ? 0 : 1;
            const bDate = dateFields.includes(b) ? 0 : 1;
            return aDate - bDate;
        });
    }, [fieldStats, dateFields]);

    // Keep only one X-axis datapoint (best business label, prefer date/time),
    // and never allow numeric metric fields on X-axis.
    const xAxisOptions = useMemo(() => {
        const nonMetricLabels = categoricalFields.filter((field) => !numericFields.includes(field));
        if (!nonMetricLabels.length) return [];
        return [nonMetricLabels[0]];
    }, [categoricalFields, numericFields]);

    const [selectedYAxis, setSelectedYAxis] = useState('');
    const [selectedXAxis, setSelectedXAxis] = useState('');
    const [isCombinedMode, setIsCombinedMode] = useState(false);

    // Hard guard: X-axis cannot be the same as Y-axis.
    const selectableXAxisOptions = useMemo(() => {
        return xAxisOptions.filter((field) => field !== selectedYAxis);
    }, [xAxisOptions, selectedYAxis]);

    // Check if we have chartable data
    const hasChartableData = numericFields.length > 0 && selectableXAxisOptions.length > 0;

    // Set default endpoint when data first loads.
    useEffect(() => {
        if (availableEndpoints.length > 0 && !selectedEndpoint) {
            setSelectedEndpoint(availableEndpoints[0]);
        }
    }, [availableEndpoints, selectedEndpoint]);

    // Reset axis selections with smart defaults when endpoint changes.
    useEffect(() => {
        const suggested = chartSuggestions.find((s) =>
            numericFields.includes(s?.y_axis) && xAxisOptions.includes(s?.x_axis) && s?.x_axis !== s?.y_axis
        );

        const bestCategory = xAxisOptions[0] || '';
        const bestMetric = numericFields
            .filter((field) => field !== bestCategory)
            .map((field) => ({
                field,
                score: getMetricQualityScore(field, fieldStats[field]?.numericValues || []),
            }))
            .sort((a, b) => b.score - a.score)[0]?.field;

        setSelectedYAxis(suggested?.y_axis || bestMetric || '');
        setSelectedXAxis(suggested?.x_axis || bestCategory || '');
    }, [selectedEndpoint, chartSuggestions, numericFields, xAxisOptions, fieldStats]);

    // Auto-correct any invalid pair where X and Y are identical.
    useEffect(() => {
        if (!selectedXAxis || !selectedYAxis) return;
        if (selectedXAxis !== selectedYAxis) return;

        const fallbackX = xAxisOptions.find((field) => field !== selectedYAxis) || '';
        if (fallbackX) {
            setSelectedXAxis(fallbackX);
            return;
        }

        const fallbackY = numericFields.find((field) => field !== selectedXAxis) || '';
        if (fallbackY) {
            setSelectedYAxis(fallbackY);
        }
    }, [selectedXAxis, selectedYAxis, xAxisOptions, numericFields]);

    // Hard guard: X-axis must never be any metric field available in Y-axis options.
    useEffect(() => {
        if (!selectedXAxis) return;
        if (!numericFields.includes(selectedXAxis)) return;

        const fallbackX = xAxisOptions.find((field) => !numericFields.includes(field)) || '';
        if (fallbackX) {
            setSelectedXAxis(fallbackX);
        }
    }, [selectedXAxis, xAxisOptions, numericFields]);

    // Get chart type from suggestions based on selected x,y pair
    const currentChartType = useMemo(() => {
        if (chartSuggestions.length > 0) {
            const match = chartSuggestions.find(
                s => s.x_axis === selectedXAxis && s.y_axis === selectedYAxis
            );
            if (match) return match.chart_type;
        }
        return 'bar'; // default fallback
    }, [selectedXAxis, selectedYAxis, chartSuggestions]);

    const getFormattedData = () => {
        if (!selectedXAxis || !selectedYAxis || !chartData || chartData.length === 0) return [];

        const dataMap = new Map();
        const isTimeSeriesAxis = dateFields.includes(selectedXAxis);
        const uniqueDateSet = new Set();

        chartData.forEach((item) => {
            const possibleDate = parseDateValue(item?.[selectedXAxis]);
            if (possibleDate) uniqueDateSet.add(toDayKey(possibleDate));
        });

        const useMonthlyBucket = isTimeSeriesAxis && uniqueDateSet.size > 24;

        chartData.forEach(item => {
            if (!item) return;
            const xValue = item[selectedXAxis];
            if (xValue === undefined || xValue === null) return;

            const yValue = parseNumericValue(item[selectedYAxis]);
            if (yValue === null) return;

            const asDate = parseDateValue(xValue);
            const xDisplay = asDate
                ? (useMonthlyBucket ? toMonthKey(asDate) : toDayKey(asDate))
                : String(xValue);

            const sortKey = asDate ? asDate.getTime() : String(xValue).toLowerCase();

            if (dataMap.has(xDisplay)) {
                const existingItem = dataMap.get(xDisplay);
                existingItem[selectedYAxis] += yValue;
            } else {
                dataMap.set(xDisplay, {
                    [selectedXAxis]: xDisplay,
                    [selectedYAxis]: yValue,
                    _sortKey: sortKey,
                });
            }
        });

        let rows = Array.from(dataMap.values());

        if (isTimeSeriesAxis) {
            rows = rows.sort((a, b) => a._sortKey - b._sortKey);
        }

        // For categorical axes with many groups, show top categories and group tail as Others.
        if (!isTimeSeriesAxis && rows.length > 12) {
            const sorted = [...rows].sort((a, b) => b[selectedYAxis] - a[selectedYAxis]);
            const top = sorted.slice(0, 11);
            const othersSum = sorted.slice(11).reduce((acc, r) => acc + r[selectedYAxis], 0);
            rows = othersSum > 0
                ? [...top, { [selectedXAxis]: 'Others', [selectedYAxis]: othersSum, _sortKey: 'others' }]
                : top;
        }

        return rows.map((row) => {
            const { _sortKey, ...rest } = row;
            return rest;
        });
    };

    const formattedData = getFormattedData();

    const combinedFormattedData = useMemo(() => {
        if (!selectedXAxis || !chartData.length || !numericFields.length) return [];

        const dataMap = new Map();
        const isTimeSeriesAxis = dateFields.includes(selectedXAxis);
        const uniqueDateSet = new Set();

        chartData.forEach((item) => {
            const possibleDate = parseDateValue(item?.[selectedXAxis]);
            if (possibleDate) uniqueDateSet.add(toDayKey(possibleDate));
        });

        const useMonthlyBucket = isTimeSeriesAxis && uniqueDateSet.size > 24;

        chartData.forEach((item) => {
            if (!item) return;
            const xValue = item[selectedXAxis];
            if (xValue === undefined || xValue === null) return;

            const asDate = parseDateValue(xValue);
            const xDisplay = asDate
                ? (useMonthlyBucket ? toMonthKey(asDate) : toDayKey(asDate))
                : String(xValue);
            const sortKey = asDate ? asDate.getTime() : String(xValue).toLowerCase();

            if (!dataMap.has(xDisplay)) {
                const seed = { [selectedXAxis]: xDisplay, _sortKey: sortKey };
                numericFields.forEach((field) => {
                    seed[field] = 0;
                });
                dataMap.set(xDisplay, seed);
            }

            const row = dataMap.get(xDisplay);
            numericFields.forEach((field) => {
                const value = parseNumericValue(item[field]);
                if (value !== null) {
                    row[field] += value;
                }
            });
        });

        let rows = Array.from(dataMap.values());
        if (isTimeSeriesAxis) {
            rows = rows.sort((a, b) => a._sortKey - b._sortKey);
        }

        if (!isTimeSeriesAxis && rows.length > 12) {
            const totalOf = (row) => numericFields.reduce((acc, field) => acc + (parseNumericValue(row[field]) || 0), 0);
            const sorted = [...rows].sort((a, b) => totalOf(b) - totalOf(a));
            const top = sorted.slice(0, 11);
            const tail = sorted.slice(11);

            const others = { [selectedXAxis]: 'Others', _sortKey: 'others' };
            numericFields.forEach((field) => {
                others[field] = tail.reduce((acc, row) => acc + (parseNumericValue(row[field]) || 0), 0);
            });

            const othersTotal = numericFields.reduce((acc, field) => acc + others[field], 0);
            rows = othersTotal > 0 ? [...top, others] : top;
        }

        return rows.map((row) => {
            const { _sortKey, ...rest } = row;
            return rest;
        });
    }, [selectedXAxis, chartData, numericFields, dateFields]);

    const businessSummary = useMemo(() => {
        if (!formattedData.length || !selectedXAxis || !selectedYAxis) return '';

        if (dateFields.includes(selectedXAxis) && formattedData.length > 1) {
            const first = formattedData[0][selectedYAxis];
            const last = formattedData[formattedData.length - 1][selectedYAxis];
            if (typeof first === 'number' && typeof last === 'number' && first !== 0) {
                const deltaPct = ((last - first) / Math.abs(first)) * 100;
                const direction = deltaPct >= 0 ? 'up' : 'down';
                return `Trend insight: ${selectedYAxis.replace(/_/g, ' ')} is ${direction} ${Math.abs(deltaPct).toFixed(1)}% across selected time.`;
            }
        }

        const top = [...formattedData]
            .sort((a, b) => b[selectedYAxis] - a[selectedYAxis])[0];
        if (top) {
            return `Top driver: ${top[selectedXAxis]} leads with ${formatNumber(top[selectedYAxis])} ${selectedYAxis.replace(/_/g, ' ')}.`;
        }
        return '';
    }, [formattedData, selectedXAxis, selectedYAxis, dateFields]);

    const combinedBusinessSummary = useMemo(() => {
        if (!combinedFormattedData.length || !selectedXAxis || !numericFields.length) return '';
        const totals = numericFields.map((field) => ({
            field,
            total: combinedFormattedData.reduce((acc, row) => acc + (parseNumericValue(row[field]) || 0), 0),
        }));
        const overall = totals.reduce((acc, item) => acc + item.total, 0);
        const topMetric = [...totals].sort((a, b) => b.total - a.total)[0];
        if (!topMetric || overall <= 0) return '';
        const share = (topMetric.total / overall) * 100;
        return `Combined insight: ${toLabel(topMetric.field)} contributes ${share.toFixed(1)}% of total metric volume.`;
    }, [combinedFormattedData, selectedXAxis, numericFields]);

    const businessMetrics = useMemo(() => {
        if (!formattedData.length || !selectedYAxis || !selectedXAxis) {
            return {
                points: 0,
                total: 0,
                average: 0,
                topLabel: '-',
                topValue: 0,
            };
        }

        const values = formattedData
            .map((row) => parseNumericValue(row[selectedYAxis]))
            .filter((v) => typeof v === 'number' && !Number.isNaN(v));

        const total = values.reduce((sum, value) => sum + value, 0);
        const average = values.length ? total / values.length : 0;
        const top = [...formattedData]
            .sort((a, b) => (parseNumericValue(b[selectedYAxis]) || 0) - (parseNumericValue(a[selectedYAxis]) || 0))[0];

        return {
            points: formattedData.length,
            total,
            average,
            topLabel: top ? String(top[selectedXAxis]) : '-',
            topValue: top ? parseNumericValue(top[selectedYAxis]) || 0 : 0,
        };
    }, [formattedData, selectedYAxis, selectedXAxis]);

    const combinedMetrics = useMemo(() => {
        if (!combinedFormattedData.length || !selectedXAxis || !numericFields.length) {
            return {
                points: 0,
                total: 0,
                average: 0,
                topLabel: '-',
                topValue: 0,
            };
        }

        const rowTotal = (row) => numericFields.reduce((acc, field) => acc + (parseNumericValue(row[field]) || 0), 0);
        const total = combinedFormattedData.reduce((acc, row) => acc + rowTotal(row), 0);
        const average = combinedFormattedData.length ? total / combinedFormattedData.length : 0;
        const top = [...combinedFormattedData].sort((a, b) => rowTotal(b) - rowTotal(a))[0];

        return {
            points: combinedFormattedData.length,
            total,
            average,
            topLabel: top ? String(top[selectedXAxis]) : '-',
            topValue: top ? rowTotal(top) : 0,
        };
    }, [combinedFormattedData, selectedXAxis, numericFields]);

    const gridStroke = alpha(theme.palette.text.secondary, 0.08);
    const axisStroke = alpha(theme.palette.text.secondary, 0.15);

    const commonXAxisProps = {
        dataKey: selectedXAxis,
        angle: -45,
        textAnchor: "end",
        height: 80,
        tick: CustomTick,
        tickLine: false,
        axisLine: { stroke: axisStroke }
    };

    const xAxisLabel = (
        <Label
            value={selectedXAxis.replace(/_/g, ' ').length > 30
                ? selectedXAxis.replace(/_/g, ' ').substring(0, 27) + '...'
                : selectedXAxis.replace(/_/g, ' ')}
            position="insideBottom" offset={-15}
            style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}
        />
    );

    const commonYAxisProps = {
        tick: CustomYTick,
        tickLine: false,
        axisLine: { stroke: axisStroke }
    };

    const yAxisLabel = (
        <Label
            value={selectedYAxis.replace(/_/g, ' ').length > 30
                ? selectedYAxis.replace(/_/g, ' ').substring(0, 27) + '...'
                : selectedYAxis.replace(/_/g, ' ')}
            angle={-90} position="insideLeft" offset={-10}
            style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}
        />
    );

    const gradientDefs = (
        <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563EB" stopOpacity={1} />
                <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0.85} />
            </linearGradient>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
            </linearGradient>
            <filter id="barShadow" x="-5%" y="-5%" width="110%" height="120%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1E3A8A" floodOpacity="0.15" />
            </filter>
        </defs>
    );

    const renderChart = () => {
        if (formattedData.length === 0) return null;

        const chartMargin = { top: 24, right: 32, left: 32, bottom: 80 };

        switch (currentChartType) {
            case 'line':
                return (
                    <LineChart data={formattedData} margin={chartMargin}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey={selectedYAxis} stroke="#2563EB"
                            strokeWidth={3} dot={{ r: 5, fill: '#fff', stroke: '#2563EB', strokeWidth: 2.5 }}
                            activeDot={{ r: 7, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}
                            animationEasing="ease-in-out" />
                    </LineChart>
                );

            case 'area':
                return (
                    <AreaChart data={formattedData} margin={chartMargin}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey={selectedYAxis} stroke="#2563EB"
                            fill="url(#areaGradient)" strokeWidth={2.5}
                            dot={{ r: 4, fill: '#fff', stroke: '#2563EB', strokeWidth: 2 }}
                            activeDot={{ r: 6, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}
                            animationEasing="ease-in-out" />
                    </AreaChart>
                );

            case 'pie':
                return (
                    <PieChart>
                        <Pie data={formattedData} dataKey={selectedYAxis} nameKey={selectedXAxis}
                            cx="50%" cy="50%" innerRadius={70} outerRadius={160}
                            paddingAngle={3} cornerRadius={6}
                            label={renderPieLabel} labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                            animationDuration={1200} animationEasing="ease-out">
                            {formattedData.map((entry) => (
                                <Cell key={`${entry[selectedXAxis]}-${entry[selectedYAxis]}`} fill={PIE_COLORS[formattedData.indexOf(entry) % PIE_COLORS.length]}
                                    stroke="none" />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ paddingTop: 16, fontSize: 12 }}
                            iconType="circle" iconSize={8}
                            formatter={LegendFormatter}
                        />
                    </PieChart>
                );

            case 'scatter':
                return (
                    <ScatterChart margin={chartMargin}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#CBD5E1', strokeDasharray: '4 4' }} />
                        <Scatter data={formattedData} fill="#2563EB"
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}>
                            {formattedData.map((entry) => (
                                <Cell key={`scatter-${entry[selectedXAxis]}-${entry[selectedYAxis]}`} fill={CHART_PALETTE[formattedData.indexOf(entry) % CHART_PALETTE.length]}
                                    r={7} />
                            ))}
                        </Scatter>
                    </ScatterChart>
                );

            case 'stacked_bar':
            case 'bar':
            default:
                return (
                    <BarChart data={formattedData} margin={chartMargin}
                        barSize={Math.min(40, Math.max(16, 600 / formattedData.length))}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />}
                            cursor={{ fill: alpha('#2563EB', 0.04), radius: 4 }} />
                        <Bar dataKey={selectedYAxis} fill="url(#barGradient)"
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}
                            animationEasing="ease-out" radius={[6, 6, 0, 0]}
                            shape={<RoundedBar />} isAnimationActive={true} />
                    </BarChart>
                );
        }
    };

    const renderCombinedChart = () => {
        if (combinedFormattedData.length === 0) return null;
        const chartMargin = { top: 24, right: 32, left: 32, bottom: 80 };

        return (
            <LineChart data={combinedFormattedData} margin={chartMargin}>
                {gradientDefs}
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                <YAxis {...commonYAxisProps}>
                    <Label
                        value="All Metrics"
                        angle={-90}
                        position="insideLeft"
                        offset={-10}
                        style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600 }}
                    />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ paddingTop: 16, fontSize: 12 }}
                    iconType="circle"
                    iconSize={8}
                    formatter={LegendFormatter}
                />
                {numericFields.map((field, index) => (
                    <Line
                        key={field}
                        type="monotone"
                        dataKey={field}
                        name={toLabel(field)}
                        stroke={CHART_PALETTE[index % CHART_PALETTE.length]}
                        strokeWidth={2.2}
                        dot={{ r: 3, fill: '#fff', stroke: CHART_PALETTE[index % CHART_PALETTE.length], strokeWidth: 1.8 }}
                        activeDot={{ r: 5, fill: CHART_PALETTE[index % CHART_PALETTE.length], stroke: '#fff', strokeWidth: 1.6 }}
                        animationDuration={1000}
                    />
                ))}
            </LineChart>
        );
    };

    const selectSx = {
        minWidth: 210,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            background: theme.palette.background.paper,
            fontSize: 13,
            '& .MuiSelect-select': { py: 1.05 },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB', borderWidth: 1.5 },
        },
        '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 500 },
        '& .MuiInputLabel-root.Mui-focused': { color: '#2563EB' },
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                borderRadius: '18px',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(160deg, rgba(17,24,39,0.98) 0%, rgba(15,23,42,0.98) 100%)'
                    : 'linear-gradient(160deg, #FCFDFF 0%, #F4F8FF 100%)',
                p: { xs: 2, md: 3 },
            }}
        >
            {/* <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between" sx={{ mb: 2.5 }}>
                <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1E3A8A' }}>
                        Business Chart View
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontSize: 12.5, color: '#64748B' }}>
                        Analyze trends, top drivers, and comparative performance from selected data points.
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                    {selectedEndpoint && (
                        <Chip
                            size="small"
                            label={`Source: ${formatEndpointLabel(selectedEndpoint)}`}
                            sx={{
                                borderRadius: '999px',
                                fontWeight: 600,
                                color: '#1E3A8A',
                                backgroundColor: alpha('#2563EB', 0.1),
                            }}
                        />
                    )}
                    <Chip
                        size="small"
                        label={`Chart: ${chartTypeLabel}`}
                        sx={{
                            borderRadius: '999px',
                            fontWeight: 600,
                            color: '#334155',
                            backgroundColor: alpha('#64748B', 0.12),
                        }}
                    />
                </Stack>
            </Stack> */}

            {!hasChartableData && (
                <Paper
                    elevation={0}
                    sx={{
                        height: 600,
                        width: '100%',
                        p: 4,
                        borderRadius: '16px',
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.02)'
                            : 'linear-gradient(145deg, #FAFBFF 0%, #F1F5F9 100%)',
                        border: `1px dashed ${alpha('#64748B', 0.28)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box sx={{ textAlign: 'center', maxWidth: 440 }}>
                        <Typography variant="h6" sx={{ mb: 1, color: '#475569', fontWeight: 600 }}>
                            No Chartable Data Available
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                            The selected source does not have compatible numeric and categorical fields.
                            {availableEndpoints.length > 1 && ' Switch the source from the control below and try again.'}
                        </Typography>

                        <Box sx={{ mt: 2.5 }}>
                            <FormControl size="small" variant="outlined" sx={selectSx}>
                                <InputLabel>Source</InputLabel>
                                <Select value={selectedEndpoint} onChange={(e) => setSelectedEndpoint(e.target.value)} label="Source">
                                    {availableEndpoints.map((endpoint) => (
                                        <MenuItem key={endpoint} value={endpoint} sx={{ fontSize: 13 }}>
                                            {formatEndpointLabel(endpoint)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Paper>
            )}

            {hasChartableData && (
                <>
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: '14px',
                            backgroundColor: alpha('#FFFFFF', theme.palette.mode === 'dark' ? 0.02 : 0.72),
                            border: `1px solid ${alpha('#94A3B8', 0.2)}`,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                                flexWrap: 'wrap',
                                '& > *': { flexGrow: { xs: 1, md: 0 }, mb: { xs: 1, md: 0 } },
                            }}
                        >
                            <FormControl size="small" variant="outlined" sx={selectSx}>
                                <InputLabel>Source</InputLabel>
                                <Select value={selectedEndpoint} onChange={(e) => setSelectedEndpoint(e.target.value)} label="Source">
                                    {availableEndpoints.map((endpoint) => (
                                        <MenuItem key={endpoint} value={endpoint} sx={{ fontSize: 13 }}>
                                            {formatEndpointLabel(endpoint)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" variant="outlined" sx={selectSx}>
                                <InputLabel>Y-Axis Metric</InputLabel>
                                <Select
                                    value={selectedYAxis}
                                    onChange={(e) => setSelectedYAxis(e.target.value)}
                                    label="Y-Axis Metric"
                                    disabled={isCombinedMode}
                                >
                                    {numericFields.map((field) => (
                                        <MenuItem key={field} value={field} sx={{ fontSize: 13 }}>
                                            {toLabel(field)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" variant="outlined" sx={selectSx}>
                                <InputLabel>X-Axis Dimension</InputLabel>
                                <Select
                                    value={selectedXAxis}
                                    onChange={(e) => setSelectedXAxis(e.target.value)}
                                    label="X-Axis Dimension"
                                    disabled={selectableXAxisOptions.length <= 1}
                                >
                                    {selectableXAxisOptions.map((field) => (
                                        <MenuItem key={field} value={field} sx={{ fontSize: 13 }}>
                                            {toLabel(field)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isCombinedMode}
                                        onChange={(e) => setIsCombinedMode(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Combined Metrics"
                                sx={{
                                    ml: { xs: 0, md: 0.5 },
                                    mr: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: isCombinedMode ? '#1E3A8A' : 'text.secondary',
                                    },
                                }}
                            />
                        </Stack>
                    </Paper>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: '12px', flex: 1, border: `1px solid ${alpha('#94A3B8', 0.18)}` }}>
                            <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>Data Points</Typography>
                            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
                                {isCombinedMode ? combinedMetrics.points : businessMetrics.points}
                            </Typography>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: '12px', flex: 1, border: `1px solid ${alpha('#94A3B8', 0.18)}` }}>
                            <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>
                                {isCombinedMode ? 'Total (All Metrics)' : `Total ${toLabel(selectedYAxis)}`}
                            </Typography>
                            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
                                {formatNumber(isCombinedMode ? combinedMetrics.total : businessMetrics.total)}
                            </Typography>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: '12px', flex: 1, border: `1px solid ${alpha('#94A3B8', 0.18)}` }}>
                            <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>Average</Typography>
                            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
                                {formatNumber(isCombinedMode ? combinedMetrics.average : businessMetrics.average)}
                            </Typography>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: '12px', flex: 1.6, border: `1px solid ${alpha('#94A3B8', 0.18)}` }}>
                            <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>Top Performer</Typography>
                            <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: '#0F172A' }}>
                                {(isCombinedMode ? combinedMetrics.topLabel : businessMetrics.topLabel)}
                                {` (${formatNumber(isCombinedMode ? combinedMetrics.topValue : businessMetrics.topValue)})`}
                            </Typography>
                        </Paper>
                    </Stack>

                    {(isCombinedMode ? combinedBusinessSummary : businessSummary) && (
                        <Box
                            sx={{
                                mb: 2,
                                px: 1.5,
                                py: 1,
                                borderRadius: '10px',
                                backgroundColor: alpha('#2563EB', 0.06),
                                border: `1px solid ${alpha('#2563EB', 0.16)}`,
                            }}
                        >
                            <Typography sx={{ fontSize: 12.5, color: '#1E3A8A', fontWeight: 500 }}>
                                {isCombinedMode ? combinedBusinessSummary : businessSummary}
                            </Typography>
                        </Box>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            height: 470,
                            width: '100%',
                            p: 2.5,
                            borderRadius: '14px',
                            background: '#FFFFFF',
                            overflow: 'hidden',
                            border: '1px solid rgba(148, 163, 184, 0.1)',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                        }}
                    >
                        {(isCombinedMode ? combinedFormattedData.length > 0 : formattedData.length > 0) ? (
                            <ResponsiveContainer width="100%" height="100%">
                                {isCombinedMode ? renderCombinedChart() : renderChart()}
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%" sx={{ borderRadius: 3 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16, opacity: 0.3 }}>
                                        <path d="M21 21H4.6C4.03995 21 3.75992 21 3.54601 20.891C3.35785 20.7951 3.20487 20.6422 3.10899 20.454C3 20.2401 3 19.9601 3 19.4V3M7 15V17M11 12V17M15 9V17M19 6V17" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                                        No data available to display
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </>
            )}
        </Paper>
    );
};

// PropTypes for components
CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

RoundedBar.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    fill: PropTypes.string
};

DynamicBarChart.propTypes = {
    toolResults: PropTypes.array
};

export default DynamicBarChart;
